import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { useRoleTestSafe } from '@/contexts/RoleTestContext';
import { DEFAULT_PERMISSIONS, type Section, type UserRole } from '@/constants/roles';

export type AppRole = 'super_admin' | 'admin' | 'engineer' | 'operations' | 'editor' | 'author';

interface RolePermission {
  role: UserRole;
  section: Section;
  has_access: boolean;
}

// Cached query for user roles - shared across all components
const fetchUserRoles = async (userId: string): Promise<AppRole[]> => {
  try {
    const data = await db.fetchAll<{ role: string }>('user_roles', 'role', {
      filters: [{ column: 'user_id', operator: 'eq', value: userId }]
    });
    return data?.map(r => r.role as AppRole) || [];
  } catch (error) {
    logger.error('[useUserRole] Error fetching roles:', error);
    throw error;
  }
};

// Cached query for role permissions - shared globally
const fetchRolePermissions = async (): Promise<RolePermission[]> => {
  try {
    const data = await db.fetchAll<RolePermission>('users_role_permissions', 'role, section, has_access');
    return data || [];
  } catch (error) {
    logger.error('[useUserRole] Error fetching permissions:', error);
    throw error;
  }
};

export function useUserRole() {
  const { user } = useAuth();
  const { testingAsRole, isTestingRole } = useRoleTestSafe();

  // Fetch roles using user.id (CASCADE trigger ensures public.users.id === auth.users.id)
  const { 
    data: actualRoles = [], 
    isLoading: isRolesLoading 
  } = useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: () => fetchUserRoles(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Check if superadmin to skip permissions fetch
  const hasSuperAdminRole = actualRoles.includes('super_admin');

  // Fetch permissions only if NOT superadmin (superadmin has full access)
  const { 
    data: permissions = [], 
    isLoading: isPermissionsLoading 
  } = useQuery({
    queryKey: ['role-permissions'],
    queryFn: fetchRolePermissions,
    enabled: !!user?.id && !hasSuperAdminRole,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Combined loading state
  const isLoading = isRolesLoading || (!hasSuperAdminRole && isPermissionsLoading);

  // Determine effective roles based on role testing
  const effectiveRoles = useMemo(() => {
    if (hasSuperAdminRole && isTestingRole && testingAsRole) {
      return [testingAsRole];
    }
    return actualRoles;
  }, [actualRoles, isTestingRole, testingAsRole, hasSuperAdminRole]);

  const hasRole = useCallback((role: AppRole): boolean => {
    return effectiveRoles.includes(role);
  }, [effectiveRoles]);

  // Check if user can access a specific section
  const canAccess = useCallback((section: Section): boolean => {
    // Super admin always has full access
    if (effectiveRoles.includes('super_admin')) return true;

    // Check permissions from DB first
    if (permissions.length > 0) {
      return effectiveRoles.some(role => {
        const perm = permissions.find(p => p.role === role && p.section === section);
        return perm?.has_access === true;
      });
    }

    // Fallback to default permissions
    return effectiveRoles.some(role => {
      const rolePerms = DEFAULT_PERMISSIONS[role as UserRole];
      return rolePerms?.includes(section);
    });
  }, [effectiveRoles, permissions]);

  // Compute all access values once and return them directly as booleans
  return useMemo(() => {
    const _canAccessOperations = canAccess('operations');
    const _canAccessPublications = canAccess('publications');
    const _canAccessComments = canAccess('comments');
    const _canAccessMedia = canAccess('media');
    const _canAccessUsers = canAccess('users');
    const _canAccessMemberships = canAccess('memberships');
    const _canAccessPages = canAccess('pages');
    const _canAccessCommunications = canAccess('communications');
    const _canAccessIntelligence = canAccess('intelligence');
    const _canAccessSettings = canAccess('settings');
    
    const _isSuperAdmin = effectiveRoles.includes('super_admin');
    const _isActuallySuperAdmin = hasSuperAdminRole;
    const _isAdmin = effectiveRoles.includes('super_admin') || effectiveRoles.includes('admin');
    const _isEditor = effectiveRoles.includes('super_admin') || effectiveRoles.includes('admin') || effectiveRoles.includes('editor');
    const _isAuthor = _canAccessPublications;
    const _isPrivilegedRole = effectiveRoles.includes('super_admin') || effectiveRoles.includes('admin') || effectiveRoles.includes('engineer');

    return {
      roles: effectiveRoles,
      actualRoles,
      permissions,
      hasRole,
      canAccess,
      // Direct boolean values (not functions)
      canAccessOperations: _canAccessOperations,
    canAccessPublications: _canAccessPublications,
    canAccessComments: _canAccessComments,
      canAccessMedia: _canAccessMedia,
      canAccessUsers: _canAccessUsers,
      canAccessMemberships: _canAccessMemberships,
      canAccessPages: _canAccessPages,
      canAccessCommunications: _canAccessCommunications,
      canAccessIntelligence: _canAccessIntelligence,
      canAccessSettings: _canAccessSettings,
      isSuperAdmin: _isSuperAdmin,
      isActuallySuperAdmin: _isActuallySuperAdmin,
      isAdmin: _isAdmin,
      isEditor: _isEditor,
      isAuthor: _isAuthor,
      isPrivilegedRole: _isPrivilegedRole,
      isLoading,
      isTestingRole
    };
  }, [effectiveRoles, actualRoles, permissions, hasRole, canAccess, hasSuperAdminRole, isLoading, isTestingRole]);
}
